// src/pages/interview/InterviewSessionDetailPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getInterviewSessionDetails } from "../../services/interviewAssistantService";
import Loader from "../../components/Loader";
import FeedbackPanel from "../../components/FeedbackPanel";
import Markdown from "react-markdown";
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  Mic,
  Brain,
  Calendar,
  Play,
  AlertCircle,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

// Helper to parse AiFeedbackJson safely
const parseAiFeedback = (jsonString) => {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Failed to parse AI Feedback JSON:", error);
    return { error: "Feedback data is corrupted.", rawFeedback: jsonString };
  }
};

const InterviewSessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionDetails, setSessionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    if (!sessionId) {
      setError("No session ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const details = await getInterviewSessionDetails(sessionId);
        setSessionDetails(details);
      } catch (err) {
        setError(err.message || "Failed to load session details.");
        console.error("Error fetching session details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [sessionId]);

  const toggleQuestionExpansion = (index) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader message="Loading session details..." />
          <p className="text-gray-400 mt-4">
            Analyzing your interview performance...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Unable to Load Session
            </h1>
            <p className="text-gray-300 mb-8">{error}</p>
            <button
              onClick={() => navigate("/dashboard/interview-history")}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:scale-105 transition-all duration-300"
            >
              Back to History
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h1 className="text-xl text-white mb-4">Session Not Found</h1>
            <p className="text-gray-400 mb-8">
              The requested interview session could not be located.
            </p>
            <button
              onClick={() => navigate("/dashboard/interview-history")}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:scale-105 transition-all duration-300"
            >
              Back to History
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { responses, cvName, createdAt, overallFeedbackText, jobDescription } =
    sessionDetails;
  const completedQuestions =
    responses?.filter((r) => r.transcribedAnswer)?.length || 0;
  const totalQuestions = responses?.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/dashboard/interview-history")}
          className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-300 group"
        >
          <ArrowLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
          <span className="font-medium">Back to Interview History</span>
        </button>

        {/* Header Section */}
        <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 p-6 sm:p-8 border-b border-slate-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  Interview Review
                </h1>
                <p className="text-gray-300">
                  Detailed analysis of your interview performance
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-slate-700/50 rounded-xl px-4 py-2">
                  <div className="text-sm text-gray-400">Completion</div>
                  <div className="text-xl font-bold text-white">
                    {totalQuestions > 0
                      ? Math.round((completedQuestions / totalQuestions) * 100)
                      : 0}
                    %
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Session Info Grid */}
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-400">
                    CV Document
                  </p>
                  <p
                    className="text-white font-medium truncate"
                    title={cvName || "N/A"}
                  >
                    {cvName || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-400">
                    Interview Date
                  </p>
                  <p className="text-white font-medium">
                    {dateFormatter.format(new Date(createdAt))}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4 md:col-span-2 xl:col-span-1">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-400">
                    Questions Answered
                  </p>
                  <p className="text-white font-medium">
                    {completedQuestions} of {totalQuestions}
                  </p>
                </div>
              </div>
            </div>

            {jobDescription && (
              <div className="mt-6 p-4 bg-slate-700/30 rounded-xl border border-slate-600/50">
                <div className="flex items-start space-x-3">
                  <Brain className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-400 mb-1">
                      Target Role
                    </p>
                    <p className="text-gray-300 text-sm line-clamp-3">
                      {jobDescription}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Questions & Answers Section */}
        {responses && responses.length > 0 ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-white flex items-center">
                <MessageSquare className="w-6 h-6 mr-3 text-blue-400" />
                Questions & Performance
              </h2>
              <div className="text-sm text-gray-400">
                {completedQuestions} answered •{" "}
                {responses.length - completedQuestions} skipped
              </div>
            </div>

            <div className="space-y-4">
              {responses.map((response, index) => {
                const feedback = parseAiFeedback(response.aiFeedbackJson);
                const isExpanded = expandedQuestions.has(index);
                const hasAnswer = Boolean(response.transcribedAnswer);

                return (
                  <div
                    key={response.id || index}
                    className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl overflow-hidden transition-all duration-300 hover:border-slate-600/50"
                  >
                    {/* Question Header */}
                    <div className="p-6 border-b border-slate-700/50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                hasAnswer
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-gray-500/20 text-gray-400"
                              }`}
                            >
                              {index + 1}
                            </div>
                            <span className="px-3 py-1 bg-slate-700/50 rounded-lg text-xs font-medium text-gray-300">
                              {response.questionType || "General"}
                            </span>
                            {hasAnswer && (
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-white leading-relaxed">
                            {response.question}
                          </h3>
                        </div>
                        {hasAnswer && (
                          <button
                            onClick={() => toggleQuestionExpansion(index)}
                            className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-sm font-medium text-gray-300 transition-colors duration-200"
                          >
                            {isExpanded ? "Collapse" : "View Details"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Answer and Feedback - Collapsible */}
                    {hasAnswer && (
                      <div
                        className={`transition-all duration-300 overflow-hidden ${
                          isExpanded
                            ? "max-h-[2000px] opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="p-6 space-y-6">
                          {/* User Answer */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
                              <Mic className="w-4 h-4 mr-2 text-blue-400" />
                              Your Response
                            </h4>
                            <div className="bg-slate-700/30 border border-slate-600/50 rounded-xl p-4">
                              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                                {response.transcribedAnswer}
                              </p>
                            </div>
                          </div>

                          {/* AI Feedback */}
                          {feedback && !feedback.error && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
                                <Brain className="w-4 h-4 mr-2 text-purple-400" />
                                AI Analysis & Feedback
                              </h4>
                              <div className="bg-slate-900/50 rounded-xl border border-slate-600/50 overflow-hidden">
                                <FeedbackPanel
                                  feedback={feedback}
                                  isReviewMode={true}
                                />
                              </div>
                            </div>
                          )}

                          {feedback?.error && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                              <div className="flex items-center text-red-400 mb-2">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                <span className="font-medium">
                                  Feedback Unavailable
                                </span>
                              </div>
                              <p className="text-red-300 text-sm">
                                Unable to display AI feedback due to data
                                formatting issues.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* No Answer State */}
                    {!hasAnswer && (
                      <div className="p-6 text-center">
                        <div className="w-12 h-12 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <MessageSquare className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-400 text-sm">
                          This question was not answered during the interview
                          session.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              No Questions Available
            </h3>
            <p className="text-gray-400">
              No questions were answered in this session, or the data is not
              available.
            </p>
          </div>
        )}

        {/* Overall Feedback Section */}
        {overallFeedbackText ? (
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600/10 to-blue-600/10 p-6 border-b border-slate-700/50">
              <h2 className="text-2xl font-semibold text-white flex items-center">
                <TrendingUp className="w-6 h-6 mr-3 text-teal-400" />
                Overall Session Feedback
              </h2>
              <p className="text-gray-300 mt-2">
                Comprehensive analysis of your interview performance
              </p>
            </div>
            <div className="p-6 sm:p-8">
              <div className="prose prose-invert prose-slate max-w-none">
                <div className="text-gray-300 leading-relaxed">
                  <Markdown>{overallFeedbackText}</Markdown>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              Overall Feedback Unavailable
            </h3>
            <p className="text-gray-400">
              No comprehensive session feedback has been generated yet.
            </p>
          </div>
        )}

        {/* Action Section */}
        <div className="text-center pt-4">
          <Link
            to="/interview/setup"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:scale-105 transition-all duration-300 shadow-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Start New Interview Practice
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InterviewSessionDetailPage;
